import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import "./Sidebar.css";
import logo from "@/assets/darpha_logo_digital.png";
import logoSmall from "@/assets/darpha_logo_digital.png"; // Asegúrate de tener un logo pequeño/icono
import { googleLogout } from "@react-oauth/google";
import { useLayout } from "@app/providers/LayoutContext"; // Importar el contexto
import { useDemo } from "@app/providers/DemoContext"; // Importar el contexto de demo
import { openSupportTicket } from "@/components/support-ticket/SupportTicket";
import apiClient from "@shared/config/axiosConfig";
import plantasService from "@shared/api/plantasService";
import {
  getUserRole,
  getUserPlanta,
  getUserName,
  clearAuthData,
} from "../../utils/authStorage";

// Componente DropdownMenu adaptado para manejar el modo colapsado
const DropdownMenu = ({ title, icon, children, isActive, headerPath }) => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { isSidebarCollapsed } = useLayout(); // Obtener estado de colapsado

  // Derivar estado abierto: sincronizar con isActive sin useEffect
  if (isActive && !isOpen) {
    setIsOpen(true);
  } else if (!isActive && isOpen && location.pathname !== "/") {
    setIsOpen(false);
  }

  const handleHeaderClick = (e) => {
    if (headerPath) {
      navigate(headerPath);
      e.stopPropagation();
    } else {
      toggleMenu();
    }
  };

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div className={`dropdown-menu ${isActive ? "active" : ""}`}>
      <div
        role="button"
        tabIndex={0}
        className="dropdown-header"
        onClick={handleHeaderClick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleHeaderClick(e);
          }
        }}
      >
        <div className="menu-items-header">
          <i className={icon}></i>
          {!isSidebarCollapsed && <span>{title}</span>}
        </div>
        {children && !isSidebarCollapsed && (
          <i
            role="button"
            tabIndex={0}
            className={`fa-solid ${
              isOpen ? "fa-chevron-up" : "fa-chevron-down"
            }`}
            onClick={(e) => {
              e.stopPropagation();
              toggleMenu();
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.currentTarget.click();
              }
            }}
          ></i>
        )}
      </div>
      {children && (
        <div
          className={`dropdown-content ${
            isOpen && !isSidebarCollapsed ? "open" : ""
          }`}
        >
          {children}
        </div>
      )}
    </div>
  );
};

// Sección Dev colapsable
const DevSection = ({ isSidebarCollapsed, navigate, location }) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleToggle = () => setIsOpen((prev) => !prev);

  return (
    <div className="dev-menu-items dev-simulated-section">
      <div
        role="button"
        tabIndex={0}
        className="dev-section-toggle"
        onClick={handleToggle}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleToggle();
          }
        }}
      >
        {!isSidebarCollapsed ? (
          <span className="dev-section-label">
            <i className="fas fa-code"></i> Desarrollador
            <i
              className={`fa-solid ${isOpen ? "fa-chevron-up" : "fa-chevron-down"} dev-section-arrow`}
            ></i>
          </span>
        ) : (
          <span className="dev-section-label-collapsed" title="Desarrollador">
            <i className="fas fa-code"></i>
          </span>
        )}
      </div>
      {(isOpen || isSidebarCollapsed) && (
        <>
          <div
            role="button"
            tabIndex={0}
            className="menu-items dev-menu-item"
            title="Dashboard Desarrollador"
            onClick={() => {
              sessionStorage.removeItem("RolDevelop");
              sessionStorage.removeItem("PlantaDevelop");
              navigate("/homedeveloper");
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") e.currentTarget.click();
            }}
            style={{ cursor: "pointer" }}
          >
            <i className="fas fa-tachometer-alt"></i>
            {!isSidebarCollapsed && <span>Dashboard</span>}
          </div>
          <NavLink
            to="/developer-dashboard"
            className="menu-items dev-menu-item"
            title="Monitoreo de Errores"
          >
            <i className="fas fa-bug"></i>
            {!isSidebarCollapsed && <span>Errores</span>}
          </NavLink>
          <NavLink
            to="/homedeveloper/tickets"
            className="menu-items dev-menu-item"
            title="Tickets de Trello"
          >
            <i className="fab fa-trello"></i>
            {!isSidebarCollapsed && <span>Tickets</span>}
          </NavLink>
          <NavLink
            to="/catalogos"
            className={`menu-items dev-menu-item ${
              location.pathname.startsWith("/catalogos") ? "active" : ""
            }`}
            title="Catálogos"
          >
            <i className="fas fa-book"></i>
          </NavLink>
          <div
            role="button"
            tabIndex={0}
            className="menu-items dev-menu-item"
            title="Cambiar Rol"
            onClick={() => {
              sessionStorage.removeItem("RolDevelop");
              sessionStorage.removeItem("PlantaDevelop");
              navigate("/homedeveloper/roles");
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") e.currentTarget.click();
            }}
            style={{ cursor: "pointer" }}
          >
            <i className="fas fa-exchange-alt"></i>
            {!isSidebarCollapsed && <span>Cambiar Rol</span>}
          </div>
        </>
      )}
    </div>
  );
};

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    isSidebarCollapsed,
    toggleSidebarCollapse,
    isDarkTheme,
    toggleTheme,
  } = useLayout();
  const { isDemoMode } = useDemo(); // Usar el contexto de demo

  // Feature de planos habilitada para la planta del supervisor
  const [supervisorPlanosEnabled, setSupervisorPlanosEnabled] = useState(false);

  // Función para determinar si una ruta está activa
  const isPathActive = (path) => {
    if (location.pathname === "/homeadmin" && path === "/homeadmin") {
      return false;
    }

    if (location.pathname === "/homerh" && path === "/homerh") {
      return false;
    }

    if (path === "/homeadmin") {
      return (
        location.pathname.startsWith("/formatos") ||
        location.pathname.startsWith("/admin/reports")
      );
    } else if (path === "/homeinspector") {
      return (
        location.pathname === "/homeinspector" ||
        location.pathname.startsWith("/inspector/reports") ||
        location.pathname.startsWith("/herramientas") ||
        location.pathname.startsWith("/formatos") ||
        location.pathname.startsWith("/mantenimiento") ||
        location.pathname.startsWith("/inspector/plan-anual") ||
        location.pathname.startsWith("/inspector/certificaciones") ||
        location.pathname.startsWith("/scanner/take") ||
        location.pathname.startsWith("/inspector/inventory") ||
        location.pathname.startsWith("/inspector/equipment") ||
        location.pathname.startsWith("/planos-interactivos")
      );
    } else if (path === "/homerh") {
      return (
        location.pathname.startsWith("/formatos") ||
        location.pathname.startsWith("/admin/reports") ||
        location.pathname.startsWith("/homerh")
      );
    }
    return location.pathname.startsWith(path);
  };

  const getRoleFromToken = () => {
    return getUserRole();
  };

  const getPlantaFromToken = () => {
    return getUserPlanta();
  };

  const getRole = () => {
    // Si el rol es 5 (desarrollador), permitir cambiar rol con RolDevelop en sessionStorage
    const tokenRole = getRoleFromToken();
    if (tokenRole === 5) {
      const rolDevelop = sessionStorage.getItem("RolDevelop");
      return rolDevelop ? parseInt(rolDevelop) : 5;
    }
    // Si el rol es 4 (default) y estamos en modo demo, mostrar como supervisor (rol 3)
    if (tokenRole === 4 && isDemoMode) {
      return 3;
    }
    return tokenRole;
  };

  const getUsername = () => {
    let userName = getUserName();

    // Si sigue siendo null o undefined
    if (!userName) {
      return "Usuario";
    }

    const acronyms = ["Ing.", "Lic.", "Doc."];
    const nameParts = userName.split(" ");
    let userNameFirst = nameParts[0];

    if (acronyms.includes(userNameFirst) && nameParts.length > 1) {
      userNameFirst += " " + nameParts[1];
    }

    if (userNameFirst.length > 10) {
      userNameFirst = userNameFirst.slice(0, 20);
    }
    return userNameFirst;
  };

  const handleLogout = async () => {
    try {
      // Llamar al API para cerrar sesión y limpiar cookies httpOnly
      await apiClient.post(
        "/logout",
        {},
        {
          skipErrorLog: true,
          skipSessionExpired: true,
        },
      );
    } catch (error) {
      console.error("Error al cerrar sesión:", error);
    }
    // Limpiar datos de usuario del storage
    clearAuthData();
    googleLogout();
    navigate("/");
  };

  const isDeveloper = getRoleFromToken() === 5;
  const currentRole = getRole();
  const menuRole = currentRole;

  // Cargar si la feature de planos está habilitada para la planta del supervisor
  useEffect(() => {
    if (menuRole !== 3 || isDeveloper) return;
    const idplanta = getPlantaFromToken();
    if (!idplanta) return;
    let cancelled = false;
    plantasService
      .getPlantaById(idplanta)
      .then((p) => {
        if (!cancelled) setSupervisorPlanosEnabled(!!p?.planos_habilitados);
      })
      .catch(() => {
        if (!cancelled) setSupervisorPlanosEnabled(false);
      });
    return () => {
      cancelled = true;
    };
  }, [menuRole, isDeveloper]);

  // Planos: dev siempre, admin/inspector siempre, supervisor solo si su planta
  // tiene la feature habilitada (gestionado por admin)
  const showPlanosOption =
    isDeveloper ||
    [1, 2].includes(menuRole) ||
    (menuRole === 3 && supervisorPlanosEnabled);

  return (
    <div className={`sidebar ${isSidebarCollapsed ? "collapsed" : ""}`}>
      {/* Botón para colapsar/expandir */}
      <div
        role="button"
        tabIndex={0}
        className="sidebar-collapse-button"
        onClick={toggleSidebarCollapse}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggleSidebarCollapse(e);
          }
        }}
      >
        <i
          className={`fa-solid ${
            isSidebarCollapsed ? "fa-angles-right" : "fa-angles-left"
          }`}
        ></i>
      </div>

      <div className="sidebar-logo">
        {isSidebarCollapsed ? (
          <img
            src={logoSmall || logo}
            alt="Darpha Logo"
            className="logo-small"
          />
        ) : (
          <img src={logo} alt="Darpha Logo" />
        )}
      </div>

      {!isSidebarCollapsed && (
        <div className="sidebar-username">{getUsername()}</div>
      )}

      <div className="sidebar-menu">
        {menuRole === 1 && (
          <>
            <NavLink
              to="/homeadmin"
              className={`menu-items ${
                location.pathname.startsWith("/homeadmin") ? "active" : ""
              }`}
              title="Home"
            >
              <i className="fa-solid fa-home"></i>
              {!isSidebarCollapsed && <span>Home</span>}
            </NavLink>

            {/* ── REPORTES ── */}
            <div className="nav-section-label">
              <span>Reportes</span>
            </div>
            <NavLink
              to="/formatos"
              className={`menu-items ${
                location.pathname.startsWith("/formatos") ? "active" : ""
              }`}
              title="Gestión de Formatos"
            >
              <i className="fa-solid fa-file-signature"></i>
              {!isSidebarCollapsed && <span>Formatos</span>}
            </NavLink>
            <NavLink
              to="/reportes"
              className={`menu-items ${
                location.pathname.startsWith("/reportes") ? "active" : ""
              }`}
              title="Reportes"
            >
              <i className="fa-solid fa-list-check"></i>
              {!isSidebarCollapsed && <span>Reportes</span>}
            </NavLink>

            {/* ── GESTIÓN ── */}
            <div className="nav-section-label">
              <span>Gestión</span>
            </div>
            <NavLink
              to="/admin/plan-anual"
              className={`menu-items ${
                location.pathname.startsWith("/admin/plan-anual")
                  ? "active"
                  : ""
              }`}
              title="Plan Anual"
            >
              <i className="fas fa-calendar-check"></i>
              {!isSidebarCollapsed && <span>Plan Anual</span>}
            </NavLink>
            <NavLink
              to="/pendientes"
              className={`menu-items ${
                location.pathname === "/pendientes" ? "active" : ""
              }`}
              title="Pendientes"
            >
              <i className="fa-solid fa-calendar-xmark"></i>
              {!isSidebarCollapsed && <span>Pendientes</span>}
            </NavLink>
            {showPlanosOption && (
              <NavLink
                to="/planos-interactivos"
                className={`menu-items ${
                  location.pathname.startsWith("/planos-interactivos")
                    ? "active"
                    : ""
                }`}
                title="Planos Interactivos"
              >
                <i className="fas fa-map-marked-alt"></i>
                {!isSidebarCollapsed && <span>Planos</span>}
              </NavLink>
            )}
            <NavLink
              to="/ai-agent"
              className={`menu-items ${
                location.pathname.startsWith("/ai-agent") ? "active" : ""
              }`}
              title="Agente IA"
            >
              <i className="fa-solid fa-robot"></i>
              {!isSidebarCollapsed && <span>DARPHA IA</span>}
            </NavLink>

            {/* ── PERSONAL ── */}
            <div className="nav-section-label">
              <span>Personal</span>
            </div>
            {!isDemoMode && (
              <NavLink
                to="/calendario-social"
                className={`menu-items ${
                  location.pathname.startsWith("/calendario-social")
                    ? "active"
                    : ""
                }`}
                title="Calendario Social"
              >
                <i className="fa-solid fa-calendar-days"></i>
                {!isSidebarCollapsed && <span>Calendario</span>}
              </NavLink>
            )}
            <NavLink
              to="/profile"
              className={`menu-items ${
                location.pathname === "/profile" ? "active" : ""
              }`}
              title="Perfil"
            >
              <i className="fas fa-user"></i>
              {!isSidebarCollapsed && <span>Perfil</span>}
            </NavLink>
          </>
        )}

        {menuRole === 2 && (
          <>
            <NavLink
              to="/homeinspector"
              className={`menu-items ${
                location.pathname === "/homeinspector" ? "active" : ""
              }`}
              title="Inicio"
            >
              <i className="fas fa-home"></i>
              {!isSidebarCollapsed && <span>Home</span>}
            </NavLink>

            {/* ── REPORTES ── */}
            <div className="nav-section-label">
              <span>Reportes</span>
            </div>
            <NavLink
              to="/inspector/reports"
              className={`menu-items ${
                location.pathname.startsWith("/inspector/reports")
                  ? "active"
                  : ""
              }`}
              title="Mis Reportes"
            >
              <i className="fa-solid fa-clipboard-check"></i>
              {!isSidebarCollapsed && <span>Mis Reportes</span>}
            </NavLink>
            <NavLink
              to="/mantenimiento"
              className={`menu-items ${
                location.pathname.startsWith("/mantenimiento") ? "active" : ""
              }`}
              title="Nuevo Reporte"
            >
              <i className="fa-solid fa-plus-square"></i>
              {!isSidebarCollapsed && <span>Nuevo Reporte</span>}
            </NavLink>
            <NavLink
              to="/formatos"
              className={`menu-items ${
                location.pathname.startsWith("/formatos") ? "active" : ""
              }`}
              title="Formatos"
            >
              <i className="fa-solid fa-file-signature"></i>
              {!isSidebarCollapsed && <span>Formatos</span>}
            </NavLink>

            {/* ── GESTIÓN ── */}
            <div className="nav-section-label">
              <span>Gestión</span>
            </div>
            <NavLink
              to="/inspector/plan-anual"
              className={`menu-items ${
                location.pathname.startsWith("/inspector/plan-anual")
                  ? "active"
                  : ""
              }`}
              title="Plan Anual"
            >
              <i className="fas fa-calendar-check"></i>
              {!isSidebarCollapsed && <span>Plan Anual</span>}
            </NavLink>
            <NavLink
              to="/pendientes"
              className={`menu-items ${
                location.pathname === "/pendientes" ? "active" : ""
              }`}
              title="Pendientes"
            >
              <i className="fa-solid fa-check-to-slot"></i>
              {!isSidebarCollapsed && <span>Pendientes</span>}
            </NavLink>
            <NavLink
              to="/inspector/inventory"
              className={`menu-items ${
                location.pathname.startsWith("/inspector/inventory")
                  ? "active"
                  : ""
              }`}
              title="Inventario"
            >
              <i className="fas fa-boxes"></i>
              {!isSidebarCollapsed && <span>Inventario</span>}
            </NavLink>
            <NavLink
              to="/inspector/equipment"
              className={`menu-items ${
                location.pathname.startsWith("/inspector/equipment")
                  ? "active"
                  : ""
              }`}
              title="Equipo PCI"
            >
              <i className="fas fa-fire-extinguisher"></i>
              {!isSidebarCollapsed && <span>Equipo PCI</span>}
            </NavLink>

            <NavLink
              to="/scanner/take"
              className={`menu-items ${
                location.pathname.startsWith("/scanner/take") ? "active" : ""
              }`}
              title="Salida de Inventario (QR)"
            >
              <i className="fas fa-qrcode"></i>
              {!isSidebarCollapsed && <span>Salida QR</span>}
            </NavLink>

            {/* ── HERRAMIENTAS ── */}
            <div className="nav-section-label">
              <span>Herramientas</span>
            </div>
            <NavLink
              to="/herramientas"
              className={`menu-items ${
                location.pathname.startsWith("/herramientas") ? "active" : ""
              }`}
              title="Préstamo de Herramientas"
            >
              <i className="fas fa-tools"></i>
              {!isSidebarCollapsed && <span>Préstamo</span>}
            </NavLink>
            {showPlanosOption && (
              <NavLink
                to="/planos-interactivos"
                className={`menu-items ${
                  location.pathname.startsWith("/planos-interactivos")
                    ? "active"
                    : ""
                }`}
                title="Planos Interactivos"
              >
                <i className="fas fa-map-marked-alt"></i>
                {!isSidebarCollapsed && <span>Planos</span>}
              </NavLink>
            )}
            <NavLink
              to="/ai-agent"
              className={`menu-items ${
                location.pathname.startsWith("/ai-agent") ? "active" : ""
              }`}
              title="Agente IA"
            >
              <i className="fa-solid fa-robot"></i>
              {!isSidebarCollapsed && <span>DARPHA IA</span>}
            </NavLink>

            {/* ── PERSONAL ── */}
            <div className="nav-section-label">
              <span>Personal</span>
            </div>
            <NavLink
              to="/inspector/certificaciones"
              className={`menu-items ${
                location.pathname.startsWith("/inspector/certificaciones")
                  ? "active"
                  : ""
              }`}
              title="Certificaciones"
            >
              <i className="fas fa-medal"></i>
              {!isSidebarCollapsed && <span>Certificaciones</span>}
            </NavLink>
            {!isDemoMode && (
              <NavLink
                to="/calendario-social"
                className={`menu-items ${
                  location.pathname.startsWith("/calendario-social")
                    ? "active"
                    : ""
                }`}
                title="Calendario Social"
              >
                <i className="fa-solid fa-calendar-days"></i>
                {!isSidebarCollapsed && <span>Calendario</span>}
              </NavLink>
            )}
            <NavLink
              to="/profile"
              className={`menu-items ${
                location.pathname === "/profile" ? "active" : ""
              }`}
              title="Perfil"
            >
              <i className="fas fa-user"></i>
              {!isSidebarCollapsed && <span>Perfil</span>}
            </NavLink>
          </>
        )}

        {menuRole === 3 && (
          <>
            <NavLink
              to="/homesupervisor"
              className={`menu-items ${
                location.pathname === "/homesupervisor" ? "active" : ""
              }`}
              title="Dashboard"
            >
              <i className="fas fa-chart-bar"></i>
              {!isSidebarCollapsed && <span>Dashboard</span>}
            </NavLink>

            {/* ── GESTIÓN ── */}
            <div className="nav-section-label">
              <span>Gestión</span>
            </div>
            <NavLink
              to="/supervisor/plan-anual"
              className={`menu-items ${
                location.pathname.startsWith("/supervisor/plan-anual")
                  ? "active"
                  : ""
              }`}
              title="Plan Anual"
            >
              <i className="fas fa-calendar-check"></i>
              {!isSidebarCollapsed && <span>Plan Anual</span>}
            </NavLink>
            <NavLink
              to="/monitor"
              className={`menu-items ${
                location.pathname.startsWith("/monitor") ? "active" : ""
              }`}
              title="Pendientes"
            >
              <i className="fa-solid fa-calendar-xmark"></i>
              {!isSidebarCollapsed && <span>Pendientes</span>}
            </NavLink>
            <NavLink
              to="/reportes"
              className={`menu-items ${
                location.pathname.startsWith("/reportes") ? "active" : ""
              }`}
              title="Reportes"
            >
              <i className="fa-solid fa-file-signature"></i>
              {!isSidebarCollapsed && <span>Reportes</span>}
            </NavLink>
            <NavLink
              to="/alarmcontrol"
              className={`menu-items ${
                location.pathname === "/alarmcontrol" ? "active" : ""
              }`}
              title="Emergencia"
            >
              <i className="fas fa-phone"></i>
              {!isSidebarCollapsed && <span>Emergencia</span>}
            </NavLink>

            {/* ── HERRAMIENTAS ── */}
            <div className="nav-section-label">
              <span>Herramientas</span>
            </div>
            {showPlanosOption && (
              <NavLink
                to="/planos-interactivos"
                className={`menu-items ${
                  location.pathname.startsWith("/planos-interactivos")
                    ? "active"
                    : ""
                }`}
                title="Planos Interactivos"
              >
                <i className="fas fa-map-marked-alt"></i>
                {!isSidebarCollapsed && <span>Planos</span>}
              </NavLink>
            )}
            <NavLink
              to="/ai-agent"
              className={`menu-items ${
                location.pathname.startsWith("/ai-agent") ? "active" : ""
              }`}
              title="Agente IA"
            >
              <i className="fa-solid fa-robot"></i>
              {!isSidebarCollapsed && <span>DARPHA IA</span>}
            </NavLink>
            <NavLink
              to="/supervisor/certificaciones"
              className={`menu-items ${
                location.pathname.startsWith("/supervisor/certificaciones")
                  ? "active"
                  : ""
              }`}
              title="Certificaciones y Perfiles"
            >
              <i className="fas fa-medal"></i>
              {!isSidebarCollapsed && <span>Certificaciones</span>}
            </NavLink>

            {/* ── PERSONAL ── */}
            <div className="nav-section-label">
              <span>Personal</span>
            </div>
            {!isDemoMode && menuRole !== 3 && (
              <NavLink
                to="/calendario-social"
                className={`menu-items ${
                  location.pathname.startsWith("/calendario-social")
                    ? "active"
                    : ""
                }`}
                title="Calendario Social"
              >
                <i className="fa-solid fa-calendar-days"></i>
                {!isSidebarCollapsed && <span>Calendario</span>}
              </NavLink>
            )}
            <NavLink
              to="/profile"
              className={`menu-items ${
                location.pathname === "/profile" ? "active" : ""
              }`}
              title="Perfil"
            >
              <i className="fas fa-user"></i>
              {!isSidebarCollapsed && <span>Perfil</span>}
            </NavLink>
          </>
        )}

        {menuRole === 7 && (
          <>
            <NavLink
              to="/homerh"
              className={`menu-items ${
                location.pathname.startsWith("/homerh") ? "active" : ""
              }`}
              title="Home - Recursos Humanos"
            >
              <i className="fa-solid fa-home"></i>
              {!isSidebarCollapsed && <span>Home</span>}
            </NavLink>

            {/* ── REPORTES ── */}
            <div className="nav-section-label">
              <span>Reportes</span>
            </div>
            <NavLink
              to="/formatos"
              className={`menu-items ${
                location.pathname.startsWith("/formatos") ? "active" : ""
              }`}
              title="Gestión de Formatos"
            >
              <i className="fa-solid fa-file-alt"></i>
              {!isSidebarCollapsed && <span>Formatos</span>}
            </NavLink>
            <NavLink
              to="/reportes"
              className={`menu-items ${
                location.pathname.startsWith("/reportes") ? "active" : ""
              }`}
              title="Reportes"
            >
              <i className="fa-solid fa-list-check"></i>
              {!isSidebarCollapsed && <span>Reportes</span>}
            </NavLink>

            {/* ── GESTIÓN ── */}
            <div className="nav-section-label">
              <span>Gestión</span>
            </div>
            <NavLink
              to="/rh/plan-anual"
              className={`menu-items ${
                location.pathname.startsWith("/rh/plan-anual") ? "active" : ""
              }`}
              title="Plan Anual"
            >
              <i className="fas fa-calendar-check"></i>
              {!isSidebarCollapsed && <span>Plan Anual</span>}
            </NavLink>
            <NavLink
              to="/pendientes"
              className={`menu-items ${
                location.pathname === "/pendientes" ? "active" : ""
              }`}
              title="Pendientes"
            >
              <i className="fa-solid fa-check-to-slot"></i>
              {!isSidebarCollapsed && <span>Pendientes</span>}
            </NavLink>
            <NavLink
              to="/ai-agent"
              className={`menu-items ${
                location.pathname.startsWith("/ai-agent") ? "active" : ""
              }`}
              title="Agente IA"
            >
              <i className="fa-solid fa-robot"></i>
              {!isSidebarCollapsed && <span>DARPHA IA</span>}
            </NavLink>

            {/* ── PERSONAL ── */}
            <div className="nav-section-label">
              <span>Personal</span>
            </div>
            {!isDemoMode && (
              <NavLink
                to="/calendario-social"
                className={`menu-items ${
                  location.pathname.startsWith("/calendario-social")
                    ? "active"
                    : ""
                }`}
                title="Calendario Social"
              >
                <i className="fa-solid fa-calendar-days"></i>
                {!isSidebarCollapsed && <span>Calendario</span>}
              </NavLink>
            )}
            <NavLink
              to="/profile"
              className={`menu-items ${
                location.pathname === "/profile" ? "active" : ""
              }`}
              title="Perfil"
            >
              <i className="fas fa-user"></i>
              {!isSidebarCollapsed && <span>Perfil</span>}
            </NavLink>
          </>
        )}

        {menuRole === 8 && (
          <>
            <NavLink
              to="/homeinstalacion"
              className={`menu-items ${
                location.pathname.startsWith("/homeinstalacion") ? "active" : ""
              }`}
              title="Home - Instalación"
            >
              <i className="fa-solid fa-home"></i>
              {!isSidebarCollapsed && <span>Home</span>}
            </NavLink>

            {/* ── HERRAMIENTAS ── */}
            <div className="nav-section-label">
              <span>Herramientas</span>
            </div>
            <NavLink
              to="/herramientas"
              className={`menu-items ${
                location.pathname.startsWith("/herramientas") ? "active" : ""
              }`}
              title="Gestión de Herramientas"
            >
              <i className="fas fa-tools"></i>
              {!isSidebarCollapsed && <span>Gestión Herram.</span>}
            </NavLink>

            {/* ── INVENTARIO ── */}
            <div className="nav-section-label">
              <span>Inventario</span>
            </div>
            <NavLink
              to="/scanner/take"
              className={`menu-items ${
                location.pathname === "/scanner/take" ? "active" : ""
              }`}
              title="Tomar Inventario"
            >
              <i className="fas fa-qrcode"></i>
              {!isSidebarCollapsed && <span>Tomar Inventario</span>}
            </NavLink>
            <NavLink
              to="/scanner/add"
              className={`menu-items ${
                location.pathname === "/scanner/add" ? "active" : ""
              }`}
              title="Agregar Inventario"
            >
              <i className="fas fa-plus-circle"></i>
              {!isSidebarCollapsed && <span>Agregar Inventario</span>}
            </NavLink>
            <NavLink
              to="/homeinstalacion/inventario"
              className={`menu-items ${
                location.pathname === "/homeinstalacion/inventario"
                  ? "active"
                  : ""
              }`}
              title="Gestionar Inventario"
            >
              <i className="fas fa-boxes"></i>
              {!isSidebarCollapsed && <span>Gestionar Inventario</span>}
            </NavLink>

            {/* ── PERSONAL ── */}
            <div className="nav-section-label">
              <span>Personal</span>
            </div>
            <NavLink
              to="/profile"
              className={`menu-items ${
                location.pathname === "/profile" ? "active" : ""
              }`}
              title="Perfil"
            >
              <i className="fas fa-user"></i>
              {!isSidebarCollapsed && <span>Perfil</span>}
            </NavLink>
          </>
        )}

        {isDeveloper && menuRole === 5 && (
          <>
            <div
              role="button"
              tabIndex={0}
              className="menu-items"
              title="Dashboard Desarrollador"
              onClick={() => {
                sessionStorage.removeItem("RolDevelop");
                sessionStorage.removeItem("PlantaDevelop");
                navigate("/homedeveloper");
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") e.currentTarget.click();
              }}
              style={{ cursor: "pointer" }}
            >
              <i className="fas fa-tachometer-alt"></i>
              {!isSidebarCollapsed && <span>Dashboard</span>}
            </div>

            {/* ── DESARROLLO ── */}
            <div className="nav-section-label">
              <span>Desarrollo</span>
            </div>
            <NavLink
              to="/developer-dashboard"
              className="menu-items"
              title="Monitoreo de Errores"
            >
              <i className="fas fa-bug"></i>
              {!isSidebarCollapsed && <span>Errores</span>}
            </NavLink>
            <NavLink
              to="/security-dashboard"
              className={`menu-items ${
                location.pathname.startsWith("/security-dashboard")
                  ? "active"
                  : ""
              }`}
              title="Security Dashboard"
            >
              <i className="fas fa-shield-alt"></i>
              {!isSidebarCollapsed && <span>Seguridad</span>}
            </NavLink>
            <NavLink
              to="/homedeveloper/agent-quality"
              className={`menu-items ${
                location.pathname.startsWith("/homedeveloper/agent-quality")
                  ? "active"
                  : ""
              }`}
              title="Calidad DARPHA IA"
            >
              <i className="fas fa-comments"></i>
              {!isSidebarCollapsed && <span>Calidad IA</span>}
            </NavLink>
            <NavLink
              to="/sessions-management"
              className={`menu-items ${
                location.pathname.startsWith("/sessions-management")
                  ? "active"
                  : ""
              }`}
              title="Gestión de Sesiones"
            >
              <i className="fas fa-user-clock"></i>
              {!isSidebarCollapsed && <span>Sesiones</span>}
            </NavLink>
            
            {/* ── SOPORTE ── */}
            <div className="nav-section-label">
              <span>Soporte</span>
            </div>
            <NavLink
              to="/homedeveloper/tickets"
              className="menu-items"
              title="Tickets de Trello"
            >
              <i className="fab fa-trello"></i>
              {!isSidebarCollapsed && <span>Tickets</span>}
            </NavLink>
            <NavLink
              to="/developer/users"
              className={`menu-items ${
                location.pathname.startsWith("/developer/users")
                  ? "active"
                  : ""
              }`}
              title="Usuarios"
            >
              <i className="fas fa-users-cog"></i>
              {!isSidebarCollapsed && <span>Usuarios</span>}
            </NavLink>

            {/* ── HERRAMIENTAS ── */}
            <div className="nav-section-label">
              <span>Herramientas</span>
            </div>
            <NavLink
              to="/catalogos"
              className={`menu-items ${
                location.pathname.startsWith("/catalogos") ? "active" : ""
              }`}
              title="Catálogos"
            >
              <i className="fas fa-book"></i>
              {!isSidebarCollapsed && <span>Catálogos</span>}
            </NavLink>
            <NavLink
              to="/ai-agent"
              className={`menu-items ${
                location.pathname.startsWith("/ai-agent") ? "active" : ""
              }`}
              title="Agente IA"
            >
              <i className="fa-solid fa-robot"></i>
              {!isSidebarCollapsed && <span>DARPHA IA</span>}
            </NavLink>
            <NavLink
              to="/planos-interactivos"
              className={`menu-items ${
                location.pathname.startsWith("/planos-interactivos")
                  ? "active"
                  : ""
              }`}
              title="Planos Interactivos"
            >
              <i className="fas fa-map-marked-alt"></i>
              {!isSidebarCollapsed && <span>Planos</span>}
            </NavLink>
            <NavLink
              to="/profile"
              className={`menu-items ${
                location.pathname === "/profile" ? "active" : ""
              }`}
              title="Perfil"
            >
              <i className="fas fa-user"></i>
              {!isSidebarCollapsed && <span>Perfil</span>}
            </NavLink>
          </>
        )}

        {[1, 2, 5, 7].includes(menuRole) && (
          <NavLink
            to="/personal-todos"
            className={`menu-items ${
              location.pathname.startsWith("/personal-todos") ? "active" : ""
            }`}
            title="Mis Tareas"
          >
            <i className="fas fa-th-large"></i>
            {!isSidebarCollapsed && <span>My Tasks</span>}
          </NavLink>
        )}

        {/* Espacio flexible que empuja los items al final */}
        <div className="sidebar-spacer"></div>

        {/* Dev items separados cuando se simula otro rol */}
        {isDeveloper && menuRole !== 5 && (
          <DevSection
            isSidebarCollapsed={isSidebarCollapsed}
            navigate={navigate}
            location={location}
          />
        )}

        {/* Cambiar Rol cuando está en vista nativa dev (sin simulación) */}
        {isDeveloper && menuRole === 5 && (
          <div className="dev-menu-items">
            <div
              role="button"
              tabIndex={0}
              className="menu-items dev-menu-item"
              title="Cambiar Rol"
              onClick={() => {
                sessionStorage.removeItem("RolDevelop");
                sessionStorage.removeItem("PlantaDevelop");
                navigate("/homedeveloper/roles");
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") e.currentTarget.click();
              }}
              style={{ cursor: "pointer" }}
            >
              <i className="fas fa-exchange-alt"></i>
              {!isSidebarCollapsed && <span>Cambiar Rol</span>}
            </div>
          </div>
        )}

        {/* Botón de soporte técnico - siempre visible */}
        <div
          role="button"
          tabIndex={0}
          className="menu-items sidebar-bottom-item support-sidebar-item"
          onClick={openSupportTicket}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              openSupportTicket(e);
            }
          }}
          title="Soporte Técnico"
        >
          <i className="fas fa-headset"></i>
          {!isSidebarCollapsed && <span>Soporte</span>}
        </div>

        {/* Botón para cambiar tema - siempre al final */}
        <div
          role="button"
          tabIndex={0}
          className={`menu-items theme-toggle sidebar-bottom-item ${
            isDarkTheme ? "active" : ""
          }`}
          onClick={() => {
            toggleTheme();
            // Feedback visual inmediato
            const sidebar = document.querySelector(".sidebar");
            if (sidebar) {
              sidebar.style.transition = "background-color 0.3s ease";
              sidebar.style.backgroundColor = !isDarkTheme
                ? "#262626"
                : "#ffffff";
              sidebar.style.color = !isDarkTheme ? "#f5f5f5" : "#171717";
              setTimeout(() => {
                sidebar.style.backgroundColor = "";
                sidebar.style.color = "";
              }, 500);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") e.currentTarget.click();
          }}
          style={{
            border: isDarkTheme
              ? "1px solid var(--red-400)"
              : "1px solid transparent",
            background: isDarkTheme
              ? "linear-gradient(rgba(204, 0, 0, 0.1), transparent)"
              : "transparent",
          }}
          title={isDarkTheme ? "Modo Claro" : "Modo Oscuro"}
        >
          <i
            className={`fa-solid ${isDarkTheme ? "fa-sun" : "fa-moon"}`}
            style={{ color: isDarkTheme ? "var(--red-400)" : "" }}
          ></i>
          {!isSidebarCollapsed && (
            <span style={{ color: isDarkTheme ? "var(--red-400)" : "" }}>
              {isDarkTheme ? "Modo Claro" : "Modo Oscuro"}
            </span>
          )}
        </div>

        {/* Botón de logout siempre al final, para todos los usuarios */}
        <div
          role="button"
          tabIndex={0}
          onClick={handleLogout}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handleLogout(e);
            }
          }}
          className="menu-items sidebar-bottom-item logout-final"
          title="Cerrar Sesión"
        >
          <i className="fa-solid fa-right-from-bracket"></i>
          {!isSidebarCollapsed && <span>Logout</span>}
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
